import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./ui/sonner";
import HomePage from "./HomePage";
import RunPage from "./RunPage";
import RunClubsPage from "./RunClubsPage";
import MyRunsPage from "./MyRunsPage";
import MyEventsPage from "./MyEventsPage";
import MySavedRunsPage from "./MySavedRunsPage";
import AuthPage from "./AuthPage";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import ProfilePage from "./ProfilePage";
import UserProfilePage from "./UserProfilePage";
import AppLinkLanding from "./AppLinkLanding";
import MessagesPage from "./MessagesPage";
import ConversationPage from "./ConversationPage";
import ClubChatPage from "./ClubChatPage";
import Layout from "./Layout";
import ConsoleLogin from "./console/ConsoleLogin";
import ConsoleLayout from "./console/ConsoleLayout";
import { useAuth } from "../contexts/AuthContext";
import { useConsoleAuth, isMasterUser } from "../contexts/ConsoleAuthContext";
import { usePWA } from "../hooks/usePWA";

function ConsoleGuard() {
  const { user } = useAuth();
  const { isAdmin, loading } = useConsoleAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-runcards-background">
        <div className="text-runcards-text-primary">Loading…</div>
      </div>
    );
  }
  if (isAdmin || isMasterUser(user)) {
    return <ConsoleLayout />;
  }
  return <Navigate to="/console/login" replace />;
}

const AppWrapper = () => {
  const { user, loading, setUser } = useAuth();
  const { isMobilePWA, isLoading: pwaLoading } = usePWA();

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-runcards-background">
        <div className="text-runcards-text-primary text-xl">Loading...</div>
      </div>
    );
  }


  // Run clubs now go directly to main app after signup
  // They can complete their clubcard from the profile page

  // Console routes: no main Layout, protected by console auth
  const consoleRoutes = (
    <>
      <Route path="/console/login" element={<ConsoleLogin />} />
      <Route path="/console/*" element={<ConsoleGuard />} />
    </>
  );

  // Show normal app for all other cases
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {consoleRoutes}
          {/* Layout wrapper includes Footer on all pages */}
          <Route element={<Layout />}>
            {/* For mobile PWA, redirect root to /run */}
            <Route 
              path="/" 
              element={<HomePage />} 
            />
            <Route path="/run" element={<RunPage />} />
            <Route path="/runclubs" element={<RunClubsPage />} />
            <Route path="/myruns" element={<MyRunsPage />} />
            <Route path="/myevents" element={<MyEventsPage />} />
            <Route path="/mysavedruns" element={<MySavedRunsPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route 
              path="/profile" 
              element={user ? <ProfilePage /> : <AuthPage />} 
            />
            <Route path="/user/:userId" element={<UserProfilePage />} />
            {/* Web fallback for shared deep links opened without the app.
                If the app is installed the OS opens it via the universal link
                and these never render. */}
            <Route path="/meeting/:id" element={<AppLinkLanding type="meeting" />} />
            <Route path="/club/:id" element={<AppLinkLanding type="club" />} />
            <Route 
              path="/messages" 
              element={user ? <MessagesPage /> : <AuthPage />} 
            />
            <Route 
              path="/messages/club/:clubId" 
              element={user ? <ClubChatPage /> : <AuthPage />} 
            />
            <Route 
              path="/messages/:conversationId" 
              element={user ? <ConversationPage /> : <AuthPage />} 
            />
          </Route>
        </Routes>
        <Toaster />
      </BrowserRouter>
    </div>
  );
};

export default AppWrapper;
